import { useCallback, useEffect, useRef, useState } from 'react';
import { LIMITS } from '../constants';
import { clearImageBlob, loadImageBlob, saveImageBlob } from '../lib/storage';

export interface LoadedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  /** For the <img> thumbnail shown in the upload card. */
  objectUrl: string;
}

export type ImageError = 'invalid' | 'tooLarge' | 'failed';

async function decode(blob: Blob): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      // Fall through to the <img> path (older Safari, exotic colour profiles).
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

function release(image: LoadedImage | null): void {
  if (!image) return;
  URL.revokeObjectURL(image.objectUrl);
  const source = image.source;
  if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) source.close();
}

export function useImage() {
  const [image, setImage] = useState<LoadedImage | null>(null);
  const [error, setError] = useState<ImageError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const current = useRef<LoadedImage | null>(null);

  const install = useCallback(async (blob: Blob, persist: boolean) => {
    const { source, width, height } = await decode(blob);
    const next: LoadedImage = { source, width, height, objectUrl: URL.createObjectURL(blob) };
    release(current.current);
    current.current = next;
    setImage(next);
    setError(null);
    if (persist) void saveImageBlob(blob);
  }, []);

  const load = useCallback(
    async (file: File | Blob): Promise<boolean> => {
      if (!file.type.startsWith('image/')) {
        setError('invalid');
        return false;
      }
      if (file.size > LIMITS.maxImageBytes) {
        setError('tooLarge');
        return false;
      }
      setIsLoading(true);
      try {
        await install(file, true);
        return true;
      } catch {
        setError('failed');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [install],
  );

  const clear = useCallback(() => {
    release(current.current);
    current.current = null;
    setImage(null);
    setError(null);
    void clearImageBlob();
  }, []);

  // Restore the previous session's image from IndexedDB on first mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const blob = await loadImageBlob();
      if (!blob || cancelled || current.current) return;
      try {
        await install(blob, false);
      } catch {
        void clearImageBlob();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [install]);

  useEffect(() => () => release(current.current), []);

  return { image, error, isLoading, load, clear, dismissError: () => setError(null) };
}
